import React from "react";
import { NavLink, useHistory, useParams } from "react-router-dom";
import { match } from "ts-pattern";

import { CTA } from "src/components/cta/cta";
import { Separator } from "src/components/separator/separator";
import { School } from "src/generated/types";
import { Routes } from "src/utils/routes";
import { ReportTabs } from "src/components/report_tabs/report_tabs";

import { ExpertiseResources } from "./sections/expertise_resources/expertise_resources";
import { Purpose } from "./sections/purpose/purpose";
import { Relationships } from "./sections/relationships/relationships";
import { TeachingAndLearning } from "./sections/teaching_and_learning/teaching_and_learning";
import { MeaningfulStrengths } from "./sections/meaningful_strengths/meaningful_strengths";
import { KeyGrowthOpportunities } from "./sections/key_growth_opportunities/key_growth_opportunities";
import { WellBeing } from "./sections/well_being/well_being";
import { Overview } from "./sections/overview/overview";
import { FSCIBrandedV2Report } from "./types";
import "../../report.scss";

/*
 * Types
 */

interface ReportDetailPageProps {
    readonly report: FSCIBrandedV2Report;
    readonly school: School;
}

type SectionPath =
    | "expertise-and-resources"
    | "overview"
    | "purpose"
    | "relationships"
    | "strength-and-growth-opportunities"
    | "teaching-and-learning"
    | "well-being";

interface SectionUrlPath {
    readonly section: SectionPath;
}

/*
 * Main
 */

export const ReportPartialPage = (props: ReportDetailPageProps): React.ReactElement => {
    const { report, school } = props;

    const history = useHistory();
    const { section } = useParams<SectionUrlPath>();

    const scrollAndPush = (toPush: string): void => {
        history.push(toPush);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const getLink = (section: string) => {
        return `/reports/${report.value.type}/${report.value.year}/${section}`;
    };

    const renderDomainLinks = (): React.ReactElement => {
        return (
            <>
                <NavLink className="step-link" exact to={getLink(Routes.section.purpose)}>
                    Purpose
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.relationships)}>
                    Relationships
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.teachingAndLearning)}>
                    Teaching &amp; Learning
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.expertiseAndResources)}>
                    Expertise &amp; Resources
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.wellBeing)}>
                    Well-Being
                </NavLink>
            </>
        );
    };

    const renderDomain = (): React.ReactElement => {
        return match(section)
            .with("expertise-and-resources", () => {
                return (
                    <>
                        <ExpertiseResources report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.wellBeing))}
                            subtitle="Next:"
                            title="Explore the Well-Being Domain"
                        />
                    </>
                );
            })
            .with("overview", () => {
                return (
                    <>
                        <Overview />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.strengthAndGrowthOpportunities))}
                            subtitle="Next:"
                            title="Explore Strengths and Growth Opportunities"
                        />
                    </>
                );
            })
            .with("purpose", () => {
                return (
                    <>
                        <Purpose report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.relationships))}
                            subtitle="Next:"
                            title="Explore the Relationships Domain"
                        />
                    </>
                );
            })
            .with("relationships", () => {
                return (
                    <>
                        <Relationships report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.teachingAndLearning))}
                            subtitle="Next:"
                            title="Explore the Teaching & Learning Domain"
                        />
                    </>
                );
            })
            .with("strength-and-growth-opportunities", () => {
                return (
                    <>
                        <MeaningfulStrengths report={report} />
                        <KeyGrowthOpportunities report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.purpose))}
                            subtitle="Next:"
                            title="Explore the Five Domains"
                        />
                    </>
                );
            })
            .with("teaching-and-learning", () => {
                return (
                    <>
                        <TeachingAndLearning report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.expertiseAndResources))}
                            subtitle="Next:"
                            title="Explore the Expertise & Resources Domain"
                        />
                    </>
                );
            })
            .with("well-being", () => {
                return (
                    <>
                        <WellBeing report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.purpose))}
                            subtitle="Next:"
                            title="Explore the Purpose Domain"
                        />
                    </>
                );
            })
            .exhaustive();
    };

    return (
        <>
            <div className="report-page-top">
                <ReportTabs currentReport={report} school={school} />
                <Separator />
                <div className="report-internal-navigation">
                    <div className="internal-nav-top-line">
                        <NavLink className="step-link" exact to={getLink(Routes.section.overview)}>
                            Overview
                        </NavLink>
                        <NavLink
                            className="step-link"
                            exact
                            to={getLink(Routes.section.strengthAndGrowthOpportunities)}
                        >
                            Strengths and Growth Opportunities
                        </NavLink>
                        <div className="clear" />
                    </div>
                    <div className="internal-nav-bottom-line">{renderDomainLinks()}</div>
                </div>
            </div>
            {renderDomain()}
            <div className="internal-nav-bottom-line center">{renderDomainLinks()}</div>
        </>
    );
};
